import React, { useEffect, useCallback } from 'react'

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from "react-router-dom";

// Components

import SectionHeading from '../../../../UI/SectionHeading/SectionHeading';
import TextInput from '../../../../UI/FormInput/TextInput';
import DropDownInput from '../../../../UI/FormInput/DropDownInput';
import NumberInput from '../../../../UI/FormInput/NumberInput';
import RadioInput from '../../../../UI/FormInput/RadioInput';
import FormInputGroup from '../../../../UI/FormInputGroup/FormInputGroup';
import Button from '../../../../UI/Button/Button';
import LocaleTypeFields from '../LocaleTypeFields';
import ButtonWithIcon from '../../../../UI/ButtonWithIcon/ButtonWithIcon';
import Loader from '../../../../UI/Loader/Loader';

// Hooks

import InsertSpouseAndRepresentativePepForRevision from '../../../../../hooks/InsertSpouseAndRepresentativePepForRevision';
import GetStatesByCountry from '../../../../../hooks/GetStatesByCountry';
import GetCitiesByState from '../../../../../hooks/GetCitiesByState';
import GetMunicipalitiesByState from '../../../../../hooks/GetMunicipalitiesByState';
import GetParishesByMunicipality from '../../../../../hooks/GetParishesByMunicipality';
import GetSectorsByState from '../../../../../hooks/GetSectorsByState';

// Services

import { consultarConyugeEnListas, consultarApoderadoEnListas, clearConsultarConyugeEnListas, clearConsultarApoderadoEnListas } from '../../../../../store/lists/lists.actions';

// Utilities

import { addItemToArray, removeItemFromArray, formatDropDownOptions } from '../../../../../utilities/helper-functions';

// Constants

import { initialValues } from '../InitialValues';

export default function KeepInTouch({ formik, formStep, setFormStep }) {
    const {
        insertSpouseRepresentativePepForRevisionIsLoading,
        insertSpouseRepresentativePepForRevisionError,
        insertSpouseRepresentativePepForRevision,
        handleInsertSpouseRepresentativePepForRevision
    } = InsertSpouseAndRepresentativePepForRevision();
    const { getStatesIsLoading, getStatesError, states, handleGetStatesByCountry } = GetStatesByCountry();
    const { getCitiesIsLoading, getCitiesError, cities, handleGetCitiesByState } = GetCitiesByState();
    const { getMunicipalitiesIsLoading, getMunicipalitiesError, municipalities, handleGetMunicipalitiesByState } = GetMunicipalitiesByState();
    const { getParishesIsLoading, getParishesError, parishes, handleGetParishesByMunicipality } = GetParishesByMunicipality();
    const { getSectorsIsLoading, getSectorsError, sectors, handleGetSectorsByState } = GetSectorsByState();

    const { state } = useLocation();

    const consultSpouseOnLists = useSelector((state) => state.lists.consultSpouseOnLists);
    const consultRepresentativeOnLists = useSelector((state) => state.lists.consultRepresentativeOnLists);

    // Dropdown Options
    const dropDownOptions = useSelector((state) => state.options.dropDownOptions);
    const navigate = useNavigate();
    const dispatch = useDispatch();


    // Consult Lists actions =========================================================================================================================


    // Handle Consult Spouse on Lists

    const handleConsultSpouseOnLists = useCallback((values) => {
        const documentId = values?.conyuge?.tipoDocumento !== 'P' ? values?.conyuge?.tipoDocumento + values?.conyuge?.cedulaRif : values?.conyuge?.tipoDocumento + values?.conyuge?.pasaporte;

        const internalListData = { DNI: documentId };
        const internationalListData = {
            nombre: values?.conyuge?.nombre + " " + values?.conyuge?.primerApellido + " " + values?.conyuge?.segundoApellido,
            identifiacion: documentId
        };

        dispatch(consultarConyugeEnListas(internalListData, internationalListData));
    }, []);


    // Check if client has spouse or representative to consult each on lists

    useEffect(() => {
        dispatch(clearConsultarConyugeEnListas());
        dispatch(clearConsultarApoderadoEnListas());
    }, [dispatch])

    useEffect(() => {
        if ((formik.values.cliente?.estadoCivil === 'C' || formik.values.cliente?.estadoCivil === 'U') && state?.userPartialData?.lists?.conyuge?.is_blocked !== 0) {
            handleConsultSpouseOnLists(formik.values);
        } else if (formik.values.hasApoderado === 'si' && state?.userPartialData?.lists?.apoderado?.is_blocked !== 0) {
            handleConsultRepresentativeOnLists(formik.values);
        }
    }, [formik.values.cliente?.estadoCivil, formik.values.hasApoderado])


    // Handle Consult Representative on Lists

    const handleConsultRepresentativeOnLists = useCallback((values) => {
        const documentId = values?.apoderado?.tipoDocumento !== 'P' ? values?.apoderado?.tipoDocumento + values?.apoderado.cedulaRif : values?.apoderado?.tipoDocumento + values?.apoderado?.pasaporte;

        const internalListData = { DNI: documentId };
        const internationalListData = {
            nombre: values?.apoderado?.nombre + " " + values?.apoderado?.primerApellido + " " + values?.apoderado?.segundoApellido,
            identifiacion: documentId
        };

        dispatch(consultarApoderadoEnListas(internalListData, internationalListData));
    }, []);


    // Consult the representative on list after consulting the spouse

    useEffect(() => {
        if ((consultSpouseOnLists.error?.codigoError === "1" || consultSpouseOnLists.data?.codigoError === "0") && formik.values?.hasApoderado === 'si') {
            handleConsultRepresentativeOnLists(formik.values);
        } else if (consultSpouseOnLists.error?.codigoError === "1" && formik.values.hasApoderado === 'no') {
            handleInsertarClienteEnListaParaRevision(formik.values, consultSpouseOnLists, consultRepresentativeOnLists);
        }
    }, [consultSpouseOnLists])


    useEffect(() => {
        if ((consultRepresentativeOnLists.error?.codigoError === "1" || consultRepresentativeOnLists.data?.codigoError === "0") && formik.values.hasApoderado === 'si') {
            handleInsertarClienteEnListaParaRevision(formik.values, consultSpouseOnLists, consultRepresentativeOnLists);
        }
    }, [consultRepresentativeOnLists]);


    // Handle Insert Client On List For Review

    const handleInsertarClienteEnListaParaRevision = useCallback((values, spouse, representative) => {
        const spouseOnList = spouse.error?.codigoError === "1";
        const representativeOnlist = representative.error?.codigoError === "1";

        if (spouseOnList || representativeOnlist) {
            handleInsertSpouseRepresentativePepForRevision(values, 'lista_internacional', spouseOnList, representativeOnlist, false, state);
        }
    }, []);

    useEffect(() => {
        if (insertSpouseRepresentativePepForRevision?.success) {
            navigate('/solicitud', { state: { userPartialData: { is_blocked: true } } });
        }
    }, [insertSpouseRepresentativePepForRevision, navigate]);


    // Localities field actions =========================================================================================================================

    // Get the States by Country

    useEffect(() => {
        if (formik.values?.direccion?.pais === '0') {
            handleGetStatesByCountry(formik.values?.direccion?.pais);
        }
        if (formik.values?.direccion?.pais) {
            const code = formik.values?.direccion?.pais;
            const item = dropDownOptions?.data?.countries?.find((item) => item.value === code);
            formik.setFieldValue(`direccion.nombrePais`, item?.name);
        }
    }, [handleGetStatesByCountry, formik.values?.direccion?.pais])


    // Get the Cities by State

    useEffect(() => {
        if (formik.values?.direccion?.entidad) {
            handleGetCitiesByState(formik.values?.direccion?.entidad);
        }
        if (formik.values?.direccion?.entidad) {
            const code = formik.values?.direccion?.entidad;
            const item = states?.find((item) => item.value === code);
            formik.setFieldValue(`direccion.nombreEntidad`, item?.name);
        }
    }, [handleGetCitiesByState, formik.values?.direccion?.entidad])


    // Get the Municipality by State 

    useEffect(() => {
        if (formik.values?.direccion?.entidad) {
            handleGetMunicipalitiesByState(formik.values?.direccion?.entidad);
        }
    }, [handleGetMunicipalitiesByState, formik.values?.direccion?.entidad])


    // Set City Name 

    useEffect(() => {
        if (formik.values?.direccion?.ciudad) {
            const code = formik.values?.direccion?.ciudad;
            const item = cities?.find((item) => item.value === code);
            formik.setFieldValue(`direccion.nombreCiudad`, item?.name);
        }
    }, [formik.values?.direccion?.ciudad])


    // Get the Parishes by Municipality 

    useEffect(() => {
        if (formik.values?.direccion?.municipio) {
            handleGetParishesByMunicipality(formik.values?.direccion?.municipio);
        }
        if (formik.values?.direccion?.municipio) {
            const code = formik.values?.direccion?.municipio;
            const item = municipalities?.find((item) => item.value === code);
            formik.setFieldValue(`direccion.nombreMunicipio`, item?.name);
        }
    }, [handleGetParishesByMunicipality, formik.values?.direccion?.municipio])


    // Get the Parish Name

    useEffect(() => {
        if (formik.values?.direccion?.parroquia) {
            const code = formik.values?.direccion?.parroquia;
            const item = parishes?.find((item) => item.value === code);
            formik.setFieldValue(`direccion.nombreParroquia`, item?.name);
        }
    }, [formik.values?.direccion?.parroquia])


    // Get the Sector Name

    useEffect(() => {
        if (formik.values?.direccion?.urbanizacion) {
            const code = formik.values?.direccion?.urbanizacion;
            const item = sectors?.find((item) => item.value === code);
            formik.setFieldValue(`direccion.nombreUrbanizacion`, item?.name);
        }
    }, [formik.values?.direccion?.urbanizacion])


    // Get the Sectors by State

    useEffect(() => {
        if (formik.values?.direccion?.entidad) {
            handleGetSectorsByState(formik.values?.direccion?.entidad);
        }
    }, [handleGetSectorsByState, formik.values?.direccion?.entidad])

    const spouseError =
        consultSpouseOnLists?.error?.codigoError !== "1"
            ? consultSpouseOnLists?.error?.descripcionError || consultSpouseOnLists?.error
            : null;

    const repError =
        consultRepresentativeOnLists?.error?.codigoError !== "1"
            ? consultRepresentativeOnLists?.error?.descripcionError || consultRepresentativeOnLists?.error
            : null;

    return (
        <>

            {(
                consultSpouseOnLists?.isLoading ||
                consultRepresentativeOnLists?.isLoading ||
                (consultSpouseOnLists?.error && consultSpouseOnLists.error?.codigoError !== "1") ||
                (consultRepresentativeOnLists?.error && consultRepresentativeOnLists.error?.codigoError !== "1")
            ) && (
                    <Loader
                        message={'Cargando...'}
                        error={spouseError || repError}
                        action={() => {
                            dispatch(clearConsultarConyugeEnListas());
                            dispatch(clearConsultarApoderadoEnListas());
                            if ((formik.values.cliente?.estadoCivil === 'C' || formik.values.cliente?.estadoCivil === 'U') && state?.userPartialData?.lists?.conyuge?.is_blocked !== 0) {
                                handleConsultSpouseOnLists(formik.values);
                            } else if (formik.values.hasApoderado === 'si' && state?.userPartialData?.lists?.apoderado?.is_blocked !== 0) {
                                handleConsultRepresentativeOnLists(formik.values);
                            }
                        }}
                    />)}

            {(insertSpouseRepresentativePepForRevisionIsLoading || insertSpouseRepresentativePepForRevisionError) &&
                <Loader
                    message={'Registrando conyuge/apoderado para revisión...'}
                    error={insertSpouseRepresentativePepForRevisionError}
                    action={() => handleInsertarClienteEnListaParaRevision(formik.values)}
                />}


            {(
                !insertSpouseRepresentativePepForRevisionIsLoading &&
                !insertSpouseRepresentativePepForRevisionError &&
                !consultSpouseOnLists?.isLoading &&
                !consultRepresentativeOnLists?.isLoading &&
                !consultSpouseOnLists?.error &&
                !consultRepresentativeOnLists?.error
            ) && (
                    <>
                        <SectionHeading text={'Aunque ya no enviamos cartas, tu dirección de habitación es imprescindible para mantener el contacto'} />

                        <FormInputGroup>

                            <DropDownInput
                                formik={formik}
                                fieldName={'direccion.pais'}
                                label={'Pais'}
                                options={formatDropDownOptions(dropDownOptions?.data?.countries)}
                                isRequired={true}
                            />

                        </FormInputGroup>

                        {(formik.values?.direccion?.pais === '' || formik.values?.direccion?.pais === '0') &&
                            <>

                                <FormInputGroup>

                                    <DropDownInput
                                        formik={formik}
                                        fieldName={'direccion.entidad'}
                                        label={'Estado'}
                                        options={formatDropDownOptions(states)}
                                        isRequired={true}
                                        disabled={states?.length === 0 || getStatesIsLoading || getStatesError}
                                        loading={getStatesIsLoading}
                                    />

                                    <DropDownInput
                                        formik={formik}
                                        fieldName={'direccion.ciudad'}
                                        label={'Ciudad'}
                                        options={formatDropDownOptions(cities)}
                                        isRequired={true}
                                        disabled={cities?.length === 0 || getCitiesIsLoading || getCitiesError}
                                        loading={getCitiesIsLoading}
                                    />

                                    <DropDownInput
                                        formik={formik}
                                        fieldName={'direccion.municipio'}
                                        label={'Municipio'}
                                        options={formatDropDownOptions(municipalities)}
                                        isRequired={true}
                                        disabled={municipalities?.length === 0 || getMunicipalitiesIsLoading || getMunicipalitiesError}
                                        loading={getMunicipalitiesIsLoading}
                                    />

                                    <DropDownInput
                                        formik={formik}
                                        fieldName={'direccion.parroquia'}
                                        label={'Parroquia'}
                                        options={formatDropDownOptions(parishes)}
                                        isRequired={true}
                                        disabled={parishes?.length === 0 || getParishesIsLoading || getParishesError}
                                        loading={getParishesIsLoading}
                                    />

                                    <DropDownInput
                                        formik={formik}
                                        fieldName={'direccion.urbanizacion'}
                                        label={'Urbanización, Sector, Barrio'}
                                        options={formatDropDownOptions(sectors)}
                                        isRequired={true}
                                        disabled={sectors?.length === 0 || getSectorsIsLoading || getSectorsError}
                                        loading={getSectorsIsLoading}
                                    />

                                </FormInputGroup>

                                <FormInputGroup>

                                    <RadioInput
                                        formik={formik}
                                        fieldName={'direccion.callecarretera'}
                                        label={'Seleccione un tipo'}
                                        options={[{ label: 'Calle', value: 'calle' }, { label: 'Carrera', value: 'carrera' }]}
                                        isGroup={true}
                                        isRequired={true}
                                    />

                                    <TextInput
                                        formik={formik}
                                        fieldName={'direccion.calle'}
                                        label={'Calle / Carrera'}
                                        maxLength={80}
                                    />

                                </FormInputGroup>

                                <FormInputGroup>

                                    <TextInput
                                        formik={formik}
                                        fieldName={'direccion.avenida'}
                                        label={'Avenida'}
                                        maxLength={80}
                                    />

                                    <TextInput
                                        formik={formik}
                                        fieldName={'direccion.esquina'}
                                        label={'Esquina'}
                                        maxLength={80}
                                    />

                                    <TextInput
                                        formik={formik}
                                        fieldName={"direccion.observaciones"}
                                        label={"Observaciones"}
                                        maxLength={120}
                                    />

                                </FormInputGroup>

                                {formik.errors?.direccion?._direccionGroup && (
                                    <small className="p-error">
                                        {formik.errors.direccion._direccionGroup}
                                    </small>
                                )}

                            </>}

                        <FormInputGroup>

                            <DropDownInput
                                formik={formik}
                                fieldName={'cliente.condicionVivienda'}
                                label={'Condición de la Vivienda'}
                                options={formatDropDownOptions(dropDownOptions?.data?.housingConditions)}
                                isRequired={true}
                            />

                            <NumberInput
                                formik={formik}
                                fieldName={'cliente.fechaDesde'}
                                label={'Tiempo de Domicilio (años)'}
                                isRequired={true}
                                min={1}
                                max={200}
                            />

                            {formik.values?.direccion?.pais === '0' &&
                                <DropDownInput
                                    formik={formik}
                                    fieldName={'direccion.tipoInmueble'}
                                    label={'Tipo de Inmueble'}
                                    options={formatDropDownOptions(dropDownOptions?.data?.propertyTypes?.filter(o => o.name !== 'CTRO. COMERCIAL' && o.name !== 'LOCAL COMERCIAL'))}
                                    isRequired={true}
                                />}

                        </FormInputGroup>

                        {(formik.values?.direccion?.pais === '0' && formik.values?.direccion?.tipoInmueble) && (
                            <FormInputGroup>

                                <TextInput
                                    formik={formik}
                                    fieldName={'direccion.nombreInmueble'}
                                    label={'Nombre / Identificación del Inmueble'}
                                    maxLength={80}
                                    isRequired={true}
                                />

                                <LocaleTypeFields formik={formik} />

                            </FormInputGroup>)}

                        {(formik.values?.direccion?.pais !== '' && formik.values?.direccion?.pais !== '0') &&
                            <TextInput
                                formik={formik}
                                fieldName={"direccion.descDireccion"}
                                label={"Dirección"}
                                maxLength={120}
                                isRequired={true}
                            />}


                        <SectionHeading text={'Redes sociales'} isSubHeading />

                        {formik.values?.socialMediaLinks?.map((socialMediaLink, index) => (
                            <div key={index}>
                                <FormInputGroup>
                                    <DropDownInput
                                        formik={formik}
                                        fieldName={`socialMediaLinks.${index}.tipoDireccion`}
                                        label={'Tipo de Dirección'}
                                        options={formatDropDownOptions(dropDownOptions?.data?.addressTypes?.filter(o => o.name !== 'DOMICILIARIA' && o.name !== 'EMPLEO' && o.name !== 'NEGOCIO PROPIO'))}
                                        showClear
                                    />

                                    <div className='social-media-address-field'>
                                        <p>@</p>
                                        <TextInput
                                            formik={formik}
                                            fieldName={`socialMediaLinks.${index}.descripcionDireccion`}
                                            label={formik.values?.socialMediaLinks[index]?.tipoDireccion === 'M' ? 'Dirección' : 'Usuario'}
                                        />
                                    </div>

                                </FormInputGroup>
                            </div>))}

                        <>
                            <ButtonWithIcon
                                action={() => addItemToArray(formik, 'socialMediaLinks', initialValues.socialMediaLinks[0])}
                                label={'Añadir Dirección'}
                            />

                            <ButtonWithIcon
                                action={() => removeItemFromArray(formik, 'socialMediaLinks')}
                                icon={'delete'}
                                disabled={formik.values?.socialMediaLinks?.length === 1}
                            />
                        </>

                        <div className='form-actions'>

                            <Button
                                action={() => dispatch(setFormStep(formStep - 1))}
                                label={'Regresa'}
                                secondaryStyle={true}
                            />

                            <Button
                                type="submit"
                                action={formik.handleSubmit}
                                label={'Continua'}
                            />

                        </div>
                    </>)}
        </>
    )
}
